import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';
import { ToastService } from './toast.service';
import { AuthService } from '@shared/services/auth.service'

export interface ServiceCallResult {
  success: boolean;
  data?: any;
  error?: any;
}

// Options for many parallel GET-like calls
export type ManyOptions = {
  mode?: 'perKey' | 'batch';
  finalize?: (dataByKey: any) => void;
};

// Entry config for each request in fetchMany
export type FetchEntryConfig = {
  serviceCall$: Observable<any>;
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
};

// Config for single fetch call
export type FetchConfig = {
  serviceCall$: Observable<any>;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onFail?: (response: any) => void;
  finalize?: () => void;
};


@Injectable({ providedIn: 'root' })
export class ApiCallService {
  isAdmin: boolean = false;

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private toastService: ToastService,
    private authService: AuthService
  ) {
    this.authService.hasAdminRole().then(isAdmin => this.isAdmin = isAdmin);
  }

  /**
   * Execute a write/push operation to server with unified error handling and success toast.
   * - Subscribes to the provided observable
   * - Uses ErrorHandlerService.handleInternal for response normalization
   * - Shows success toast when no error
   * - Resolves with { success, data | error }
   */
  execute(serviceCall$: Observable<any>, successMessage: string): Promise<ServiceCallResult> {
    return new Promise((resolve) => {
      serviceCall$.subscribe({
        next: (response) => {
          const { hasError, data } = this.errorHandlerService.handleInternal(response);
          if (!hasError) {
            this.toastService.showSuccess(successMessage);
            resolve({ success: true, data });
          } else {
            resolve({ success: false, error: response });
          }
        },
        error: (error) => {
          // For thrown/network errors, still resolve as failure
          this.toastService.showError('Có lỗi xảy ra khi thực hiện thao tác',
            undefined,
            this.isAdmin ? JSON.stringify(error, null, 2) : undefined);
          resolve({ success: false, error });
        }
      });
    });
  }

  /**
   * Execute multiple write/push operations in parallel.
   * - Normalizes each response using handleInternal
   * - Shows a single success toast if ALL succeed (no thrown errors and no hasError)
   * - Options behavior mirrors fetchMany (mode: 'perKey' | 'batch', finalize callback)
   */
  executeMany(
    serviceCalls$: Observable<any>[],
    successMessage?: string,
    options?: ManyOptions
  ): Promise<{ results: ServiceCallResult[]; hadThrown: boolean }> {
    return new Promise((resolve) => {
      if (!serviceCalls$ || serviceCalls$.length === 0) {
        if (successMessage) { this.toastService.showSuccess(successMessage); }
        options?.finalize?.({});
        resolve({ results: [], hadThrown: false });
        return;
      }

      const wrapped = serviceCalls$.map((sc$) =>
        sc$.pipe(
          map((response) => ({ ok: true, response })),
          catchError((error) => of({ ok: false, error }))
        )
      );

      forkJoin(wrapped).subscribe({
        next: (entries: any[]) => {
          const mode = options?.mode ?? 'perKey';

          if (mode === 'perKey') {
            let hadThrown = false;
            const results: ServiceCallResult[] = entries.map((e: any) => {
              if (e?.ok) {
                const { hasError, data } = this.errorHandlerService.handleInternal(e.response);
                if (!hasError) return { success: true, data };
                return { success: false, error: e.response };
              } else {
                hadThrown = true;
                return { success: false, error: e?.error };
              }
            });

            const allSuccess = results.every(r => r.success);
            if (allSuccess && successMessage) {
              this.toastService.showSuccess(successMessage);
            }

            const bag: any = {};
            results.forEach((r, idx) => { if (r.success) bag[idx] = r.data; });
            options?.finalize?.(bag);
            resolve({ results, hadThrown });
            return;
          }

          // Batch mode: avoid per-item handleInternal; do a single consolidated batch handling
          const resultsByKey: any = {};
          entries.forEach((e: any, idx: number) => { resultsByKey[idx] = e; });
          const outcome = this.errorHandlerService.handleInternalBatch(resultsByKey as any);

          // Determine hadThrown and build results from the batch outcome to avoid duplicate toasts
          const hadThrown = entries.some((e: any) => !e?.ok);
          const results: ServiceCallResult[] = entries.map((e: any, idx: number) => {
            if (!e?.ok) { return { success: false, error: e?.error }; }
            if (Object.prototype.hasOwnProperty.call(outcome.dataByKey, String(idx))) {
              return { success: true, data: outcome.dataByKey[idx] };
            }
            return { success: false, error: e.response };
          });

          const allSuccess = !outcome.hasAnyError;
          if (allSuccess && successMessage) {
            this.toastService.showSuccess(successMessage);
          }

          options?.finalize?.(outcome.dataByKey as any);
          resolve({ results, hadThrown });
        },
        error: () => {
          options?.finalize?.({});
          // Should never happen because each observable is wrapped, but guard anyway
          resolve({ results: [], hadThrown: true });
        }
      });
    });
  }


  /** Single call with unified error-handling hook for GET-like requests (config-object API) */

  fetch(cfg: FetchConfig): void {
    cfg.serviceCall$.subscribe({
      next: (response) => {
        const { hasError, data } = this.errorHandlerService.handleInternal(response);
        if (!hasError) { cfg.onSuccess?.(data); }
        else { cfg.onFail?.(response); }
        cfg.finalize?.();
      },
      error: (error) => {
        this.toastService.showError('Có lỗi xảy ra khi thực hiện thao tác',
          undefined,
          this.isAdmin ? JSON.stringify(error, null, 2) : undefined);
        cfg.onError?.(error);
        cfg.finalize?.();
      }
    });
  }

  /** Multiple calls with optional per-key or batch behavior for GET-like requests (config-object API) */
  fetchMany(
    requests: Record<string, FetchEntryConfig>,
    options?: ManyOptions
  ): void {
    const mode = options?.mode ?? 'perKey';

    const safeObject: Record<string, Observable<any>> = {};
    for (const [key, cfg] of Object.entries(requests)) {
      safeObject[key] = cfg.serviceCall$.pipe(
        map((response) => ({ ok: true, key, response })),
        catchError((error) => of({ ok: false, key, error }))
      );
    }

    forkJoin(safeObject).subscribe({
      next: (resultsByKey) => {
        if (mode === 'perKey') {
          const bag: any = {};
          for (const [key, result] of Object.entries(resultsByKey)) {
            const entry = requests[key];
            if ((result as any)?.ok) {
              const { hasError, data } = this.errorHandlerService.handleInternal((result as any).response);
              if (!hasError) {
                bag[key] = data;
                entry?.onSuccess?.(data);
              }
            } else {
              this.errorHandlerService.handleInternal((result as any).error);
              entry?.onError?.((result as any).error);
            }
          }
          options?.finalize?.(bag);
          return;
        }

        // Batch mode: one consolidated outcome
        const outcome = this.errorHandlerService.handleInternalBatch(resultsByKey as any);
        options?.finalize?.(outcome.dataByKey as any);
      },
      error: (fatalError) => {
        this.toastService.showError('Có lỗi xảy ra khi thực hiện thao tác',
          undefined,
          this.isAdmin ? JSON.stringify(fatalError, null, 2) : undefined);
        options?.finalize?.({});
      }
    });
  }
}

